import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { LEAD_CLASSIFICATIONS } from "../../api/leads";
import { LEAD_STAGES } from "../../pages/leadPipeline";
import { EMPTY_COUNTRY, type LeadFilterCriteria, type OwnerOption } from "../../pages/leadsFilter";
import { countryName, flagEmoji } from "../../i18n/countries";
import { Select } from "../Select";
import Ic from "../icons.jsx";

/**
 * Inline Lidovi (leads) filter bar — search / stage /
 * owner / country / classification, plus a shown/total summary and a "clear"
 * action. Mirrors OffersFilterBar's layout and visual language (same card,
 * field labels, search icon and Select styling) so both internal-sales lists
 * feel identical, without a giant sidebar.
 */
export type LeadsFilterBarProps = {
  criteria: LeadFilterCriteria;
  onSearchChange: (v: string) => void;
  onStageChange: (v: string) => void;
  onOwnerChange: (v: string) => void;
  onCountryChange: (v: string) => void;
  onClassificationChange: (v: string) => void;
  owners: OwnerOption[];
  /** Distinct country codes present in the loaded list (see leadsFilter.distinctCountries). */
  countries: string[];
  showOwnerFilter: boolean;
  shownCount: number;
  totalCount: number;
  filtersActive: boolean;
  onClear: () => void;
};

export function LeadsFilterBar({
  criteria, onSearchChange, onStageChange, onOwnerChange, onCountryChange, onClassificationChange,
  owners, countries, showOwnerFilter, shownCount, totalCount, filtersActive, onClear,
}: LeadsFilterBarProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";

  return (
    <section style={card}>
      <div style={filterRow}>
        <FilterField label={t("leads.filter.search", "Search")}>
          <div style={{ position: "relative" }}>
            <span style={searchIcon}><Ic.search /></span>
            <input
              value={criteria.search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t("leads.filter.searchPlaceholder", "Firm, contact, email, phone…")}
              className="input"
              style={{ width: 220, paddingLeft: 30 }}
            />
          </div>
        </FilterField>

        <FilterField label={t("leads.filter.stage", "Stage")}>
          <Select<string>
            value={criteria.stage}
            onChange={onStageChange}
            ariaLabel={t("leads.filter.stage", "Stage")}
            style={{ width: 180 }}
            options={[
              { value: "", label: t("leads.filter.stageAll", "All stages") },
              ...LEAD_STAGES.map((s) => ({ value: s, label: t(`leads.stage.${s}`, s) })),
            ]}
          />
        </FilterField>

        {showOwnerFilter && (
          <FilterField label={t("leads.filter.owner", "Owner")}>
            <Select<string>
              value={criteria.ownerId}
              onChange={onOwnerChange}
              ariaLabel={t("leads.filter.owner", "Owner")}
              style={{ width: 180 }}
              options={[
                { value: "", label: t("leads.filter.ownerAll", "All owners") },
                ...owners.map((o) => ({ value: o.id, label: o.name })),
              ]}
            />
          </FilterField>
        )}

        <FilterField label={t("leads.filter.country", "Country")}>
          <Select<string>
            value={criteria.country}
            onChange={onCountryChange}
            ariaLabel={t("leads.filter.country", "Country")}
            style={{ width: 180 }}
            options={[
              { value: "", label: t("leads.filter.countryAll", "All countries") },
              ...countries.map((code) =>
                code === EMPTY_COUNTRY
                  ? { value: EMPTY_COUNTRY, label: "—" }
                  : { value: code, label: countryName(code, locale), left: flagEmoji(code) },
              ),
            ]}
          />
        </FilterField>

        <FilterField label={t("leads.filter.classification", "Classification")}>
          <Select<string>
            value={criteria.classification}
            onChange={onClassificationChange}
            ariaLabel={t("leads.filter.classification", "Classification")}
            style={{ width: 160 }}
            options={[
              { value: "", label: t("leads.filter.classificationAll", "All") },
              ...LEAD_CLASSIFICATIONS.map((c) => ({ value: c, label: t(`leads.classification.${c}`, c) })),
            ]}
          />
        </FilterField>
      </div>

      <div style={filterSummary}>
        <span style={{ color: "var(--ink-3)", fontSize: 12.5 }}>
          {t("leads.filter.shown", { shown: shownCount, total: totalCount, defaultValue: "Showing {{shown}} of {{total}}" })}
        </span>
        {filtersActive && (
          <button type="button" className="btn ghost sm" onClick={onClear}>
            <Ic.x /> {t("leads.filter.clear", "Clear filters")}
          </button>
        )}
      </div>
    </section>
  );
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

// Mirrors OffersFilterBar's style tokens, kept local so the leads module stays
// self-contained (no cross-import of the offers component's exported styles).
const card: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-md)",
  boxShadow: "var(--shadow-1)",
  padding: 18,
};
const filterRow: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end" };
const filterSummary: CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
  marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)",
};
const fieldLabel: CSSProperties = {
  display: "block", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4,
};
const searchIcon: CSSProperties = { position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", width: 15, height: 15, display: "inline-flex", color: "var(--ink-3)", pointerEvents: "none" };
